import { supabase } from "@/lib/supabase-client";
import { InvestigationType } from "@/types/db/investigations";
import { FileMetadata } from "@/types/files";

export const investigationService = {
  async getInvestigations(): Promise<InvestigationType[]> {
    const { data, error } = await supabase
      .from("investigations")
      .select("name, id, description, status, incidents, created_at")
      .order("created_at", { ascending: false });
    if (error) {
      throw Error(error.message);
    }
    // Table listing selects a subset of columns; cast to the full row type it consumes.
    return (data ?? []) as unknown as InvestigationType[];
  },

  async getInvestigationById(id: string): Promise<InvestigationType> {
    const { data, error } = await supabase.from("investigations").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }
    return data as InvestigationType;
  },

  async addInvestigation(
    investigation: Partial<InvestigationType> & { tenant_id: string },
  ): Promise<InvestigationType> {
    const { data, error } = await supabase.from("investigations").insert([investigation]).select().single();

    if (error) {
      throw Error(error.message);
    }
    return data as InvestigationType;
  },

  async updateInvestigation(id: string, patch: Partial<InvestigationType>): Promise<InvestigationType> {
    const { data, error } = await supabase.from("investigations").update(patch).eq("id", id).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as InvestigationType;
  },

  async deleteInvestigation(id: string): Promise<boolean> {
    const { error } = await supabase.from("investigations").delete().eq("id", id);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async deleteInvestigationsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.from("investigations").delete().in("id", ids);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async saveRowPatches(patches: Partial<InvestigationType> & { id: string }[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        const { error } = await supabase.from("investigations").update(patch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async getFiles(investigationId: string): Promise<FileMetadata[]> {
    // Get tenant_id from investigation record
    const { data: tenantData, error: tenantError } = await supabase
      .from("investigations")
      .select("tenant_id")
      .eq("id", investigationId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get investigation tenant: ${tenantError?.message || "Investigation not found"}`);
    }

    // List files from storage bucket
    const storagePath = `${tenantData.tenant_id}/${investigationId}`;
    const { data: files, error: listError } = await supabase.storage.from("investigations").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list investigation files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    // Convert storage files to FileMetadata
    const fileMetadata: FileMetadata[] = files.map((file) => {
      // Parse fileId from filename (format: {fileId}-{filename})
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "investigations", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown", // Storage doesn't track uploader, would need database for this
      };
    });

    return fileMetadata;
  },

  async deleteFile(investigationId: string, fileId: string): Promise<boolean> {
    // Get tenant_id from investigation record
    const { data: tenantData, error: tenantError } = await supabase
      .from("investigations")
      .select("tenant_id")
      .eq("id", investigationId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get investigation tenant: ${tenantError?.message || "Investigation not found"}`);
    }

    // List files from storage to find the one to delete
    const storagePath = `${tenantData.tenant_id}/${investigationId}`;
    const { data: files, error: listError } = await supabase.storage.from("investigations").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list investigation files: ${listError.message}`);
    }

    // Find the file that starts with the fileId
    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    // Delete from storage
    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("investigations").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(investigationId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("investigations")
      .select("tenant_id")
      .eq("id", investigationId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get investigation tenant: ${tenantError?.message || "Investigation not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${investigationId}`;
    const { data: files, error: listError } = await supabase.storage.from("investigations").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list investigation files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("investigations").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "investigations",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },
};
